import * as React from 'react'

import { Check } from 'lucide-react'

import { AppContext } from '@/components/base/appcontext'
import { v1Options } from '@/lib/api/pocket'
import { cn } from '@/lib/utils'

type BaseRadioProps = {
    name: string
    data?: v1Options
    value?: string
    placeholder?: string
    isHorisontal?: boolean
    disabled?: boolean
    required?: boolean
    flexible?: boolean
    skipTranslate?: boolean
    /** `list`: full-width tappable rows with a check mark (onboarding). */
    variant?: 'chip' | 'list'
    /** Id of the element that names the group (a visible question). */
    labelledBy?: string
    /**
     * Called when the user toggles a radio selection (including unselect).
     *  - `param name`: the `props.name` of this RadioGroup
     *  - `param value`: the final chosen value (or `undefined` if unselected)
     */
    onChange?: (
        e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>,
        name: string,
        value?: string
    ) => Promise<void>
    className?: string
}

export default function BaseRadio(props: BaseRadioProps) {
    const appContext = React.useContext(AppContext)
    const dataKeys = Object.keys(props.data?.options || {})
    const isList = props.variant === 'list'

    const [selectedValue, setSelectedValue] = React.useState<
        string | undefined
    >(props.value)

    React.useEffect(() => {
        setSelectedValue(props.value)
    }, [props.value])

    async function handleToggle(
        e: React.MouseEvent | React.ChangeEvent<HTMLInputElement>,
        clickedValue?: string
    ) {
        const newValue =
            clickedValue === selectedValue ? undefined : clickedValue
        setSelectedValue(newValue)

        if (props.onChange) {
            await props.onChange(e, props.name, newValue)
        }
    }

    return (
        <div
            role="radiogroup"
            aria-labelledby={props.labelledBy}
            className={cn(
                isList ? 'w-full' : 'gap-2 m-2',
                props.isHorisontal ? 'flex flex-row' : 'flex flex-col',
                props.className
            )}
        >
            <div
                className={cn(
                    isList && 'flex flex-col gap-2',
                    props.flexible
                        ? 'flex flex-row flex-wrap justify-start gap-2'
                        : ''
                )}
            >
                {dataKeys.map((key, idx) => {
                    const radioValue = props.data?.options[key]
                    const isChecked = radioValue === selectedValue

                    return (
                        <div
                            key={idx + key}
                            role="radio"
                            aria-checked={isChecked}
                            tabIndex={0}
                            className={cn(
                                isList
                                    ? 'flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-base font-medium outline-none transition-[background-color,border-color,transform] duration-150 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:active:scale-100'
                                    : 'rounded-lg border-2 px-2 py-1 m-1 text-sm font-medium transition-colors',
                                'cursor-pointer select-none',
                                isList
                                    ? isChecked
                                        ? 'border-primary bg-primary/5 text-foreground'
                                        : 'border-border bg-card text-foreground hover:bg-muted/60'
                                    : isChecked
                                      ? 'border-primary text-foreground bg-primary/5'
                                      : 'border-border text-muted-foreground bg-secondary/50 hover:bg-secondary hover:text-foreground',
                                props.disabled &&
                                    'pointer-events-none opacity-50',
                                props.disabled &&
                                    isChecked &&
                                    'border-primary/40'
                            )}
                            onClick={async (event) => {
                                if (props.disabled) return
                                event.preventDefault()
                                await handleToggle(event, radioValue)
                            }}
                            onKeyDown={async (event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    if (props.disabled) return
                                    event.preventDefault()
                                    await handleToggle(
                                        event as unknown as React.MouseEvent,
                                        radioValue
                                    )
                                }
                            }}
                        >
                            {props.skipTranslate
                                ? key
                                : appContext.geti18n('options.text.' + key)}
                            {isList && isChecked && (
                                <Check
                                    aria-hidden
                                    className="size-[18px] shrink-0 text-primary"
                                />
                            )}
                            {props.required && (
                                <input
                                    type="radio"
                                    name={props.name}
                                    value={radioValue}
                                    checked={isChecked}
                                    required={props.required}
                                    onChange={() => {}}
                                    className="sr-only"
                                    tabIndex={-1}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
